// src/modules/captazap/components/LeadDetails.jsx

import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { api } from "../../../api";
import LogsModalLead from "../../../Components/LogsModalLead";
import {
  FaUserCircle,
  FaWhatsapp,
  FaInstagram,
  FaFacebookMessenger,
  FaTelegramPlane,
  FaPhoneAlt,
  FaBuilding,
  FaHistory,
  FaTag,
  FaLock,
  FaExclamationTriangle,
  FaInbox,
  FaShieldAlt,
  FaExternalLinkAlt,
  FaExchangeAlt,
  FaUserTie,
  FaChartLine,
  FaBullhorn,
  FaCalendarAlt,
  FaCheckCircle,
  FaCommentDots,
  FaPlusCircle,
  FaUserTag,
  FaRedoAlt,
  FaDollarSign,
  FaClipboardList,
  FaHandshake,
  FaUsers,
} from "react-icons/fa";

export default function LeadDetails({
  conversa,
  empresaId,
  usuarioLogado,
  usuarios = [],
  fontes = [],
  campanhas = [],
  procedimentos = [],
  regioes = [],
  dentistas = [],
  onConverterLead,
  onAgendarTarefa,
  onAdicionarTag,
  onVincularLead,
  onSolicitarTransferencia,
  onIniciarConversa,
  onAbrirTarefas,
  onAbrirInteracoes,
  onLeadAtualizada,
}) {
  const isDev = Boolean(import.meta.env?.DEV);

  const [editandoLead, setEditandoLead] = useState(false);
  const [salvandoLead, setSalvandoLead] = useState(false);
  const [acoesRapidasAberto, setAcoesRapidasAberto] = useState(false);
  const [historicoLeadAberto, setHistoricoLeadAberto] = useState(false);
  const [formEdicaoLead, setFormEdicaoLead] = useState({
    id_procedimento: "",
    potencial: "",
    status_negociacao: "",
    total_orcamento: "",
    id_dentista: "",
    id_fonte_lead: "",
    id_regiao: "",
    id_campanha: "",
  });

  if (!conversa) {
    return <EmptyLeadDetails />;
  }

  const conversaEmpresaId = conversa.empresa_id || conversa.empresaId;

  const pertenceEmpresa =
    empresaId &&
    (String(conversaEmpresaId) === String(empresaId) ||
      (!conversaEmpresaId && isDev));

  if (!pertenceEmpresa) {
    return (
      <EmpresaMismatch
        conversa={conversa}
        empresaId={empresaId}
        conversaEmpresaId={conversaEmpresaId}
      />
    );
  }

  const telefoneOperacional =
    conversa.numero ||
    conversa.telefone ||
    conversa.phone ||
    conversa.whatsapp ||
    "";

  const leadsVinculadas = getLeadsVinculadasLeadDetails(conversa).filter(
    (item) => leadPertenceAoTelefoneLeadDetails(item, telefoneOperacional),
  );
  const telefoneCompartilhado = leadsVinculadas.length > 1;
  const leadContextoId = getLeadContextoIdLeadDetails(conversa);
  const leadContexto = leadContextoId
    ? leadsVinculadas.find(
        (item) =>
          String(getLeadIdLeadDetails(item) || "") === String(leadContextoId),
      ) || null
    : null;

  const leadBaseRaw =
    conversa.lead || conversa.lead_dados || conversa.leadDados || null;
  const leadBase =
    leadBaseRaw &&
    leadPertenceAoTelefoneLeadDetails(leadBaseRaw, telefoneOperacional)
      ? leadBaseRaw
      : null;

  // Regra definitiva do CaptaZap:
  // - telefone é a conversa operacional;
  // - lead_contexto_id é o paciente fixo deste chamado;
  // - se o telefone tem várias leads e não existe contexto, não escolhemos uma automaticamente.
  const contextoLeadObrigatorio = Boolean(
    conversa.contexto_lead_obrigatorio ||
    conversa.contextoLeadObrigatorio ||
    (telefoneCompartilhado && !leadContexto),
  );

  const lead = leadContexto || (!telefoneCompartilhado ? leadBase : null);

  const leadId =
    getLeadIdLeadDetails(lead) ||
    (!telefoneCompartilhado
      ? conversa.lead_contexto_id ||
        conversa.leadContextoId ||
        conversa.lead_id ||
        conversa.leadId
      : null) ||
    null;

  const jaTemLead = Boolean(leadId && lead);

  const nome =
    lead?.nome_lead ||
    lead?.nome ||
    (contextoLeadObrigatorio
      ? "Selecione o paciente em contexto"
      : conversa.nome || conversa.numero || "Contato sem nome");

  const telefone =
    telefoneOperacional ||
    lead?.telefone1 ||
    lead?.telefone ||
    lead?.celular ||
    lead?.whatsapp ||
    "Não informado";

  const canal = conversa.canal || "whatsapp";
  const canalStyle = getCanalStyle(canal);

  const empresaNome =
    conversa.empresa_nome ||
    conversa.empresaNome ||
    lead?.empresa_nome ||
    lead?.empresaNome ||
    `Empresa ${empresaId}`;

  const captadorId =
    lead?.id_usuario ||
    lead?.captador_id ||
    lead?.usuario_criador_id ||
    conversa.lead_captador_id ||
    conversa.leadCaptadorId ||
    conversa.dono_lead_id ||
    conversa.donoLeadId ||
    null;

  const usuarioId = usuarioLogado?.id || usuarioLogado?.usuario_id || null;

  const captadorNome =
    lead?.captador_nome ||
    lead?.usuario_nome ||
    lead?.usuario_criador_nome ||
    conversa.lead_captador_nome ||
    conversa.leadCaptadorNome ||
    conversa.dono_lead_nome ||
    conversa.donoLeadNome ||
    getNomeUsuario(captadorId, usuarios) ||
    (captadorId ? `Usuário ${captadorId}` : "Não definido");

  const leadEhMinha =
    jaTemLead &&
    captadorId &&
    usuarioId &&
    String(captadorId) === String(usuarioId);

  const leadDeOutroCaptador =
    jaTemLead &&
    captadorId &&
    usuarioId &&
    String(captadorId) !== String(usuarioId);

  const transferenciaPendente =
    Boolean(conversa.transferencia_pendente) ||
    Boolean(conversa.transferenciaPendente) ||
    Boolean(lead?._solicitacao_transferencia_pendente) ||
    Boolean(lead?.transferencia_pendente) ||
    String(lead?.status_transferencia || "").toLowerCase() === "pendente";

  const leadReativavel =
    Boolean(conversa.lead_reativavel) ||
    Boolean(conversa.reativacao_pendente) ||
    Boolean(lead?._captaZap_reativavel) ||
    String(conversa.status || "").toLowerCase() === "lead_reativavel";

  const temConversaAtiva = getTemConversaAtiva(conversa);

  const statusNegociacao = limparStatus(
    lead?.status_negociacao ||
      conversa.status_negociacao ||
      conversa.statusNegociacao ||
      conversa.status ||
      "Sem status",
  );

  const statusStyle = getStatusStyle(statusNegociacao);
  const StatusIcon = statusStyle.icon;

  const statusAgendamento =
    lead?.status_agendamento ||
    conversa.status_agendamento ||
    conversa.statusAgendamento ||
    null;

  const valorOportunidade =
    lead?.total_oportunidade ??
    lead?.valor_oportunidade ??
    lead?.valor_negociacao ??
    lead?.total_orcamento ??
    lead?.valor_orcamento ??
    conversa.valor_oportunidade ??
    conversa.total_orcamento ??
    conversa.valor ??
    0;

  const totalContratado =
    lead?.total_contratado || conversa.total_contratado || 0;

  const fonte =
    lead?.nome_fonte ||
    lead?.fonte ||
    conversa.fonte ||
    getNomeById(lead?.id_fonte_lead, fontes, ["nome"]) ||
    conversa.origem ||
    formatCanal(canal);

  const campanha =
    lead?.nome_campanha ||
    conversa.nome_campanha ||
    getNomeById(lead?.id_campanha, campanhas, ["nome"]) ||
    null;

  const potencial = lead?.potencial || conversa.potencial || null;

  const ultimaInteracao =
    conversa.ultima_interacao ||
    conversa.ultimaInteracao ||
    lead?.ultima_interacao ||
    getUltimoItem(conversa.interacoes || lead?.interacoes, [
      "criado_em",
      "created_at",
      "data",
      "data_interacao",
    ]);

  const proximaTarefa =
    conversa.proxima_tarefa ||
    conversa.proximaTarefa ||
    lead?.proxima_tarefa ||
    getProximaTarefa(conversa.tarefas || lead?.tarefas);

  const ultimoAgendamento =
    conversa.ultimo_agendamento ||
    conversa.ultimoAgendamento ||
    lead?._ultimo_agendamento ||
    lead?.ultimo_agendamento ||
    getUltimoItem(conversa.agendamentos || lead?.agendamentos, [
      "data_agendamento",
      "created_at",
      "criado_em",
    ]);

  const actionContext = {
    empresaId,
    conversaId: conversa.id,
    telefone: conversa.numero || telefone,
    canal,
    leadId,
    lead_contexto_id: leadId,
    leadContextoId: leadId,
    leads_vinculadas: leadsVinculadas,
    leadsVinculadas: leadsVinculadas,
    telefoneCompartilhado,
    contextoLeadObrigatorio,
    captadorId,
    captadorNome,
    usuarioId,
    lead,
    temConversaAtiva,
    leadReativavel,
  };

  const leadHistorico = leadId
    ? {
        ...(lead || {}),
        id: lead?.id || leadId,
        lead_id: lead?.lead_id || leadId,
        nome_lead: lead?.nome_lead || lead?.nome || nome,
        nome: lead?.nome || lead?.nome_lead || nome,
        telefone1: lead?.telefone1 || lead?.telefone || telefone,
        telefone: lead?.telefone || lead?.telefone1 || telefone,
        empresa_id: lead?.empresa_id || conversa?.empresa_id || empresaId,
        usuario_criador_id:
          lead?.usuario_criador_id ||
          lead?.id_usuario ||
          lead?.captador_id ||
          conversa?.lead_captador_id ||
          conversa?.dono_lead_id ||
          captadorId,
        id_usuario:
          lead?.id_usuario ||
          lead?.captador_id ||
          lead?.usuario_criador_id ||
          conversa?.lead_captador_id ||
          conversa?.dono_lead_id ||
          captadorId,
        criado_em:
          lead?.criado_em || conversa?.criado_em || conversa?.created_at,
      }
    : null;

  const dataCriacaoLead = getDataCriacaoLead(conversa, lead);
  const criadorLeadNome = getCriadorLeadNome({
    conversa,
    lead,
    usuarios,
    fallback: captadorNome,
  });
  const empresaNomeVisivel = getEmpresaNomeVisivel(empresaNome, empresaId);
  const iniciaisLead = getIniciaisNome(nome);
  const telefoneFormatado = formatTelefoneBRLeadDetails(telefone);

  const leadUrl = getLeadUrl(leadId, lead, conversa);

  const abrirLeadCompleta = () => {
    if (leadUrl) {
      window.open(leadUrl, "_blank", "noopener,noreferrer");
      return;
    }

    onVincularLead?.(conversa, actionContext);
  };

  const abrirEdicaoLead = () => {
    if (!lead) return;

    setAcoesRapidasAberto(true);
    setFormEdicaoLead({
      id_procedimento: lead?.id_procedimento || "",
      potencial: lead?.potencial || "",
      status_negociacao: lead?.status_negociacao || "",
      total_orcamento: formatMoneyInput(
        lead?.total_orcamento ||
          lead?.valor_oportunidade ||
          lead?.valor_orcamento ||
          conversa?.valor_oportunidade ||
          "",
      ),
      id_dentista: lead?.id_dentista || "",
      id_fonte_lead: lead?.id_fonte_lead || "",
      id_regiao: lead?.id_regiao || "",
      id_campanha: lead?.id_campanha || "",
    });

    setEditandoLead(true);
  };

  const cancelarEdicaoLead = () => {
    setEditandoLead(false);
  };

  const salvarEdicaoLead = async () => {
    if (!leadId || !lead) return;

    const status = String(formEdicaoLead.status_negociacao || "").trim();

    if (!status) {
      toast.error("Informe o status da negociação.");
      return;
    }

    setSalvandoLead(true);

    try {
      const payload = montarPayloadAtualizacaoLead({
        lead,
        conversa,
        empresaId,
        usuarioLogado,
        formEdicaoLead,
      });

      await api.put(`/leads/${leadId}`, payload);

      const leadAtualizada = {
        ...lead,
        ...payload,
        _captaZap_reativavel: false,
      };

      onLeadAtualizada?.(leadAtualizada, conversa);
      toast.success("Lead atualizada com sucesso.");
      setEditandoLead(false);
    } catch (err) {
      console.error("❌ Erro ao editar lead pelo CaptaZap:", err);
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data?.erro ||
          err?.response?.data?.error ||
          "Erro ao atualizar lead.",
      );
    } finally {
      setSalvandoLead(false);
    }
  };

  return (
    <aside className="flex h-full w-full flex-col bg-white">
      <header className="relative shrink-0 overflow-hidden border-b-2 border-blue-200 bg-gradient-to-br from-blue-950 via-blue-800 to-indigo-700 px-4 py-4 text-white shadow-[0_18px_45px_rgba(30,64,175,0.30)]">
        <div className="absolute -right-12 -top-16 h-40 w-40 rounded-full bg-white/12 blur-2xl" />
        <div className="absolute -bottom-20 left-8 h-44 w-44 rounded-full bg-cyan-300/18 blur-3xl" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-white/30" />

        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.16 }}
          className="relative"
        >
          <div className="flex items-start gap-3">
            <div className="relative shrink-0">
              {conversa.foto ? (
                <img
                  src={conversa.foto}
                  className="h-14 w-14 rounded-3xl border border-white/30 object-cover shadow-[0_14px_32px_rgba(15,23,42,0.35)] ring-4 ring-white/15"
                  alt={nome}
                />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-3xl border border-white/25 bg-white/16 text-lg font-black uppercase tracking-wide text-white shadow-[0_14px_32px_rgba(15,23,42,0.35)] ring-4 ring-white/10 backdrop-blur">
                  {iniciaisLead}
                </div>
              )}

              <span
                className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-blue-900 bg-white shadow-md"
                title={formatCanal(canal)}
              >
                <CanalIcon canal={canal} size={12} />
              </span>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10px] font-black uppercase tracking-[0.18em] text-blue-100/80">
                    Identidade da lead
                  </div>

                  <h2 className="mt-1 line-clamp-2 text-base font-black leading-tight text-white drop-shadow-sm">
                    {nome}
                  </h2>
                </div>

                <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-white/25 bg-white/14 px-2.5 py-1 text-[10px] font-black text-white shadow-sm backdrop-blur">
                  <CanalIcon canal={canal} size={11} />
                  {formatCanal(canal)}
                </span>
              </div>

              <div className="mt-3 border-t border-white/18 pt-3">
                <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px] font-semibold text-blue-50/90">
                  <span className="inline-flex min-w-0 items-center gap-1.5">
                    <FaPhoneAlt size={10} className="shrink-0 text-cyan-100" />
                    <span className="truncate" title={telefoneFormatado}>
                      {telefoneFormatado}
                    </span>
                  </span>

                  <span className="hidden h-1 w-1 rounded-full bg-blue-100/45 sm:inline-flex" />

                  <span className="inline-flex min-w-0 items-center gap-1.5">
                    <FaCalendarAlt
                      size={10}
                      className="shrink-0 text-cyan-100"
                    />
                    <span className="text-blue-100/70">Criada em</span>
                    <span
                      className="truncate font-bold text-white"
                      title={dataCriacaoLead || "Não informado"}
                    >
                      {dataCriacaoLead || "Não informado"}
                    </span>
                  </span>
                </div>

                <div className="mt-2 flex min-w-0 items-center gap-1.5 text-[12px] font-semibold text-blue-50/90">
                  <FaUserTie size={10} className="shrink-0 text-cyan-100" />
                  <span className="text-blue-100/70">Captador</span>
                  <span
                    className="min-w-0 truncate font-bold text-white"
                    title={
                      criadorLeadNome || captadorNome || "Não identificado"
                    }
                  >
                    {criadorLeadNome || captadorNome || "Não identificado"}
                  </span>
                </div>

                {empresaNomeVisivel ? (
                  <div className="mt-1.5 flex min-w-0 items-center gap-1.5 text-[11px] font-semibold text-blue-100/70">
                    <FaBuilding
                      size={9}
                      className="shrink-0 text-cyan-100/80"
                    />
                    <span className="truncate" title={empresaNomeVisivel}>
                      {empresaNomeVisivel}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </motion.div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto bg-[#F8FAFC] px-5 py-5">
        {contextoLeadObrigatorio ? (
          <CleanNotice
            tone="amber"
            icon={<FaExclamationTriangle />}
            title="Selecione o paciente em contexto"
            text="Este WhatsApp possui mais de uma lead vinculada. Defina no chamado qual paciente é o atendimento atual antes de executar ações sensíveis."
            chip={`${leadsVinculadas.length} leads neste WhatsApp`}
          />
        ) : jaTemLead ? (
          <OwnershipCard
            leadEhMinha={leadEhMinha}
            leadDeOutroCaptador={leadDeOutroCaptador}
            transferenciaPendente={transferenciaPendente}
            captadorNome={captadorNome}
            leadReativavel={leadReativavel}
          />
        ) : (
          <CleanNotice
            tone="amber"
            icon={<FaExclamationTriangle />}
            title="Contato ainda sem lead vinculada"
            text="Antes de criar uma nova lead, confira duplicidade pelo telefone na empresa ativa."
          />
        )}

        {leadsVinculadas.length > 1 ? (
          <LeadsVinculadasNotice
            leads={leadsVinculadas}
            leadContextoId={leadContextoId}
          />
        ) : null}

        {jaTemLead && !leadReativavel && !temConversaAtiva && (
          <CleanNotice
            tone="emerald"
            icon={<FaCommentDots />}
            title="Lead sem conversa ativa"
            text="Este contato já existe no CaptaCRM. Você pode iniciar uma conversa pelo CaptaZap."
          />
        )}

        <section className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
          <SectionTitle icon={<FaChartLine />} title="Resumo operacional" />

          <div className="mt-4 grid grid-cols-2 gap-2">
            <MiniMetric
              icon={<StatusIcon size={13} />}
              label="Status"
              value={statusNegociacao}
              className={statusStyle.metric}
            />

            <MiniMetric
              icon={<FaDollarSign size={12} />}
              label="Oportunidade"
              value={moeda(valorOportunidade)}
              className="border-emerald-200 bg-emerald-50 text-emerald-800"
            />

            <MiniMetric
              icon={<FaUserTie size={12} />}
              label="Captador"
              value={jaTemLead ? captadorNome : "Sem lead"}
              className="border-blue-200 bg-blue-50 text-blue-800"
            />

            <MiniMetric
              icon={<FaBullhorn size={12} />}
              label="Fonte"
              value={fonte || "Não informada"}
              className="border-orange-200 bg-orange-50 text-orange-800"
            />
          </div>

          {Number(toMoneyNum(totalContratado)) > 0 && (
            <div className="mt-3 rounded-2xl border border-emerald-200 bg-white px-3 py-3 text-xs font-semibold text-emerald-700">
              Valor já contratado:{" "}
              <span className="font-black">{moeda(totalContratado)}</span>
            </div>
          )}
        </section>

        {jaTemLead && editandoLead && (
          <InlineLeadEditCard
            form={formEdicaoLead}
            setForm={setFormEdicaoLead}
            salvando={salvandoLead}
            procedimentos={procedimentos}
            dentistas={dentistas}
            fontes={fontes}
            regioes={regioes}
            campanhas={campanhas}
            onSalvar={salvarEdicaoLead}
            onCancelar={cancelarEdicaoLead}
          />
        )}

        <section className="mt-4 rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
          <SectionTitle
            icon={<FaClipboardList />}
            title="Próximos movimentos"
          />

          <div className="mt-4 space-y-3">
            <OperationalCard
              icon={<FaHistory />}
              title="Última interação"
              item={ultimaInteracao}
              fallback="Nenhuma interação registrada."
              dateFields={["criado_em", "created_at", "data", "data_interacao"]}
              textFields={["descricao", "observacao", "texto", "tipo"]}
            />

            <OperationalCard
              icon={<FaCalendarAlt />}
              title="Próxima tarefa"
              item={proximaTarefa}
              fallback="Nenhuma tarefa futura."
              dateFields={["data_vencimento", "data", "created_at"]}
              textFields={["titulo", "descricao", "observacao", "status"]}
            />

            <OperationalCard
              icon={<FaCheckCircle />}
              title="Último agendamento"
              item={ultimoAgendamento}
              fallback="Nenhum agendamento encontrado."
              dateFields={["data_agendamento", "created_at", "criado_em"]}
              textFields={["status_agendamento", "observacao", "descricao"]}
            />
          </div>

          {jaTemLead && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => onAbrirInteracoes?.(conversa, actionContext)}
                className="flex h-9 items-center justify-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-3 text-xs font-bold text-blue-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-blue-100"
              >
                <FaHistory size={12} />
                Interações
              </button>

              <button
                type="button"
                onClick={() => onAbrirTarefas?.(conversa, actionContext)}
                className="flex h-9 items-center justify-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-3 text-xs font-bold text-amber-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-amber-100"
              >
                <FaClipboardList size={12} />
                Tarefas
              </button>

              <button
                type="button"
                onClick={() => setHistoricoLeadAberto(true)}
                className="col-span-2 flex h-10 items-center justify-center gap-2 rounded-2xl border border-violet-200 bg-violet-50 px-3 text-xs font-bold text-violet-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-violet-100"
              >
                <FaHistory size={12} />
                Histórico completo da lead
              </button>
            </div>
          )}
        </section>

        {jaTemLead && (
          <section className="mt-4 rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
            <SectionTitle icon={<FaTag />} title="Contexto rápido" />

            <div className="mt-4 space-y-2">
              <InfoPill label="Agendamento" value={statusAgendamento} />
              <InfoPill label="Potencial" value={potencial} />
              <InfoPill label="Campanha" value={campanha} />
            </div>
          </section>
        )}

        <section className="mt-4 rounded-[28px] border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white text-blue-700 ring-1 ring-blue-200">
              <FaShieldAlt size={14} />
            </span>

            <div>
              <h3 className="text-sm font-bold text-slate-900">
                Atendimento liberado com rastreabilidade
              </h3>

              <p className="mt-1 text-xs leading-5 text-slate-600">
                Responda o contato para não travar a operação. Se a lead for de
                outro captador, solicite transferência apenas quando precisar
                assumir a oportunidade.
              </p>
            </div>
          </div>
        </section>
      </div>

      {historicoLeadAberto && leadHistorico?.id && (
        <LogsModalLead
          lead={leadHistorico}
          usuarios={usuarios || []}
          onClose={() => setHistoricoLeadAberto(false)}
        />
      )}

      <footer className="shrink-0 border-t border-slate-200 bg-white px-5 py-3 shadow-[0_-10px_30px_rgba(15,23,42,0.035)]">
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setAcoesRapidasAberto((aberto) => !aberto)}
            className="flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-left transition hover:border-blue-200 hover:bg-blue-50"
          >
            <span className="flex items-center gap-2">
              <span className="text-[11px] font-black uppercase tracking-wide text-slate-500">
                Ações rápidas
              </span>

              {editandoLead && (
                <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700 ring-1 ring-blue-200">
                  editando
                </span>
              )}
            </span>

            <span className="text-[11px] font-black uppercase tracking-wide text-slate-400">
              {acoesRapidasAberto ? "Recolher ▲" : "Abrir ▼"}
            </span>
          </button>

          {acoesRapidasAberto && (
            <div className="space-y-2">
              {jaTemLead && !temConversaAtiva && (
                <button
                  type="button"
                  onClick={() => onIniciarConversa?.(conversa, actionContext)}
                  className="flex h-10 w-full items-center justify-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 text-xs font-bold text-emerald-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-emerald-100"
                >
                  <FaCommentDots size={13} />
                  Iniciar conversa
                </button>
              )}

              {jaTemLead ? (
                <button
                  type="button"
                  onClick={editandoLead ? salvarEdicaoLead : abrirEdicaoLead}
                  disabled={salvandoLead}
                  className="flex h-10 w-full items-center justify-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-4 text-xs font-bold text-blue-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaExternalLinkAlt size={12} />
                  {salvandoLead
                    ? "Salvando..."
                    : editandoLead
                      ? "Salvar lead"
                      : "Editar lead"}
                </button>
              ) : null}

              {jaTemLead ? (
                <button
                  type="button"
                  onClick={abrirLeadCompleta}
                  className="flex h-10 w-full items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-xs font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800"
                >
                  <FaExternalLinkAlt size={11} />
                  Ficha completa
                </button>
              ) : null}

              {!jaTemLead && !contextoLeadObrigatorio ? (
                <button
                  type="button"
                  onClick={() => onConverterLead?.(conversa, actionContext)}
                  className="flex h-10 w-full items-center justify-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 text-xs font-bold text-emerald-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-emerald-100"
                >
                  <FaPlusCircle size={13} />
                  Converter em Lead
                </button>
              ) : null}

              {contextoLeadObrigatorio ? (
                <button
                  type="button"
                  disabled
                  className="flex h-10 w-full cursor-not-allowed items-center justify-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 text-xs font-bold text-amber-700 opacity-80"
                  title="Escolha a lead em contexto antes de criar/alterar vínculos."
                >
                  <FaExclamationTriangle size={13} />
                  Contexto obrigatório
                </button>
              ) : null}

              {leadDeOutroCaptador && (
                <button
                  type="button"
                  disabled={transferenciaPendente}
                  onClick={() =>
                    onSolicitarTransferencia?.(conversa, actionContext)
                  }
                  className="flex h-10 w-full items-center justify-center gap-2 rounded-2xl border border-orange-200 bg-orange-50 px-4 text-xs font-bold text-orange-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-orange-100 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
                >
                  <FaExchangeAlt size={13} />
                  {transferenciaPendente
                    ? "Transferência pendente"
                    : "Solicitar transferência"}
                </button>
              )}

              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => onAgendarTarefa?.(conversa, actionContext)}
                  className="flex h-10 items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-3 text-xs font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800"
                >
                  <FaCalendarAlt size={12} />
                  Agendar Lead
                </button>

                <button
                  type="button"
                  onClick={() => onAdicionarTag?.(conversa, actionContext)}
                  className="flex h-10 items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-3 text-xs font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800"
                >
                  <FaUserTag size={12} />
                  Tag
                </button>
              </div>
            </div>
          )}
        </div>
      </footer>
    </aside>
  );
}

function InlineLeadEditCard({
  form,
  setForm,
  salvando,
  procedimentos = [],
  dentistas = [],
  fontes = [],
  regioes = [],
  campanhas = [],
  onSalvar,
  onCancelar,
}) {
  const statusOptions = [
    "Prospectando",
    "Negociando",
    "Pendente",
    "Fechado",
    "Fechado Parcial",
    "Perdido",
    "Migrou Concorrência",
  ];

  const potencialOptions = ["Alto", "Médio", "Baixo"];

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <section className="mb-4 rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
      <SectionTitle icon={<FaExternalLinkAlt />} title="Edição rápida" />

      <div className="mt-4 grid grid-cols-1 gap-3">
        <EditSelect
          label="Procedimento"
          value={form.id_procedimento}
          onChange={(value) => handleChange("id_procedimento", value)}
          disabled={salvando}
          options={procedimentos}
          getLabel={(item) =>
            item?.nome || item?.procedimento || `Procedimento ${item?.id}`
          }
        />

        <div className="grid grid-cols-2 gap-3">
          <EditSelect
            label="Potencial"
            value={form.potencial}
            onChange={(value) => handleChange("potencial", value)}
            disabled={salvando}
            options={potencialOptions.map((value) => ({
              id: value,
              nome: value,
            }))}
            getLabel={(item) => item?.nome}
            emptyLabel="Não informado"
          />

          <EditSelect
            label="Negociação"
            value={form.status_negociacao}
            onChange={(value) => handleChange("status_negociacao", value)}
            disabled={salvando}
            options={statusOptions.map((value) => ({ id: value, nome: value }))}
            getLabel={(item) => item?.nome}
            emptyLabel="Selecione"
          />
        </div>

        <label className="block">
          <span className="text-[11px] font-black uppercase tracking-wide text-slate-400">
            Oportunidade
          </span>
          <input
            value={form.total_orcamento}
            onChange={(event) =>
              handleChange(
                "total_orcamento",
                formatMoneyInput(event.target.value),
              )
            }
            disabled={salvando}
            placeholder="R$ 0,00"
            className="mt-1 h-11 w-full rounded-2xl border border-slate-300 bg-white px-3 text-sm font-bold text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 disabled:bg-slate-100"
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <EditSelect
            label="Dentista"
            value={form.id_dentista}
            onChange={(value) => handleChange("id_dentista", value)}
            disabled={salvando}
            options={dentistas}
            getLabel={(item) =>
              item?.profissional || item?.nome || `Dentista ${item?.id}`
            }
          />

          <EditSelect
            label="Fonte"
            value={form.id_fonte_lead}
            onChange={(value) => handleChange("id_fonte_lead", value)}
            disabled={salvando}
            options={fontes}
            getLabel={(item) =>
              item?.nome || item?.fonte || `Fonte ${item?.id}`
            }
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <EditSelect
            label="Região"
            value={form.id_regiao}
            onChange={(value) => handleChange("id_regiao", value)}
            disabled={salvando}
            options={regioes}
            getLabel={(item) =>
              item?.regiao || item?.nome || `Região ${item?.id}`
            }
          />

          <EditSelect
            label="Campanha"
            value={form.id_campanha}
            onChange={(value) => handleChange("id_campanha", value)}
            disabled={salvando}
            options={campanhas}
            getLabel={(item) =>
              item?.nome || item?.campanha || `Campanha ${item?.id}`
            }
          />
        </div>
      </div>

      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={onCancelar}
          disabled={salvando}
          className="text-xs font-bold text-slate-400 transition hover:text-slate-700 disabled:opacity-60"
        >
          Cancelar edição
        </button>
      </div>
    </section>
  );
}

function EditSelect({
  label,
  value,
  onChange,
  disabled,
  options = [],
  getLabel,
  emptyLabel = "Não informado",
}) {
  return (
    <label className="block">
      <span className="text-[11px] font-black uppercase tracking-wide text-slate-400">
        {label}
      </span>
      <select
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        className="mt-1 h-11 w-full rounded-2xl border border-slate-300 bg-white px-3 text-sm font-bold text-slate-800 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 disabled:bg-slate-100"
      >
        <option value="">{emptyLabel}</option>
        {(options || []).map((item) => (
          <option
            key={item?.id ?? item?.value ?? item?.nome}
            value={item?.id ?? item?.value ?? item?.nome}
          >
            {getLabel ? getLabel(item) : item?.nome || item?.label || item?.id}
          </option>
        ))}
      </select>
    </label>
  );
}

function LeadsVinculadasNotice({ leads = [], leadContextoId = null }) {
  const lista = Array.isArray(leads) ? leads.filter(Boolean) : [];
  if (lista.length <= 1) return null;

  return (
    <section className="mb-4 rounded-[28px] border border-violet-200 bg-violet-50 p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white text-violet-700 ring-1 ring-violet-200">
          <FaUsers size={14} />
        </span>

        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold text-slate-900">
            Leads vinculadas a este WhatsApp
          </h3>
          <p className="mt-1 text-xs leading-5 text-slate-600">
            O atendimento usa uma única conversa por telefone. A lead marcada
            como contexto é o paciente ativo deste chamado.
          </p>

          <div className="mt-3 space-y-1.5">
            {lista.map((lead) => {
              const leadId = getLeadIdLeadDetails(lead);
              const ativo =
                leadContextoId &&
                String(leadId || "") === String(leadContextoId);
              return (
                <div
                  key={leadId || getNomeLeadVinculadaLeadDetails(lead)}
                  className={`flex items-center justify-between gap-2 rounded-2xl border px-3 py-2 text-xs ${
                    ativo
                      ? "border-violet-300 bg-white text-violet-800"
                      : "border-violet-100 bg-white/70 text-slate-600"
                  }`}
                >
                  <span className="min-w-0 truncate font-bold">
                    {getNomeLeadVinculadaLeadDetails(lead)}
                  </span>
                  <span className="shrink-0 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-black text-violet-700">
                    {ativo ? "Contexto" : `Lead #${leadId || "—"}`}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function OwnershipCard({
  leadEhMinha,
  leadDeOutroCaptador,
  transferenciaPendente,
  captadorNome,
  leadReativavel,
}) {
  if (leadReativavel) {
    return null;
  }

  if (leadEhMinha) {
    return (
      <CleanNotice
        tone="emerald"
        icon={<FaHandshake />}
        title="Sua lead"
        text="Esta oportunidade está vinculada ao seu usuário como captador responsável."
      />
    );
  }

  if (leadDeOutroCaptador) {
    return (
      <CleanNotice
        tone="orange"
        icon={<FaUserTie />}
        title="Lead de outro captador"
        text={`Dono atual: ${captadorNome}. Você pode atender, mas solicite transferência para assumir a oportunidade.`}
        chip={
          transferenciaPendente
            ? "Transferência em análise"
            : "Transferência disponível"
        }
      />
    );
  }

  return (
    <CleanNotice
      tone="slate"
      icon={<FaUserTie />}
      title="Dono não identificado"
      text="A lead está vinculada, mas o captador responsável não foi identificado."
    />
  );
}

function CleanNotice({ tone = "slate", icon, title, text, chip }) {
  const tones = {
    emerald: {
      box: "border-emerald-200 bg-emerald-50",
      icon: "text-emerald-700 ring-emerald-200",
      chip: "border-emerald-200 bg-white text-emerald-700",
    },
    orange: {
      box: "border-orange-200 bg-orange-50",
      icon: "text-orange-700 ring-orange-200",
      chip: "border-orange-200 bg-white text-orange-700",
    },
    amber: {
      box: "border-amber-200 bg-amber-50",
      icon: "text-amber-700 ring-amber-200",
      chip: "border-amber-200 bg-white text-amber-700",
    },
    slate: {
      box: "border-slate-200 bg-white",
      icon: "text-slate-500 ring-slate-200",
      chip: "border-slate-200 bg-slate-50 text-slate-600",
    },
  };

  const config = tones[tone] || tones.slate;

  return (
    <section
      className={`mb-4 rounded-[28px] border p-4 shadow-sm ${config.box}`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white ring-1 ${config.icon}`}
        >
          {icon}
        </span>

        <div className="min-w-0">
          <h3 className="text-sm font-bold text-slate-900">{title}</h3>

          <p className="mt-1 text-xs leading-5 text-slate-600">{text}</p>

          {chip && (
            <span
              className={`mt-3 inline-flex rounded-full border px-2.5 py-1 text-[11px] font-bold ${config.chip}`}
            >
              {chip}
            </span>
          )}
        </div>
      </div>
    </section>
  );
}

function EmptyLeadDetails() {
  return (
    <div className="flex h-full w-full items-center justify-center bg-white px-6">
      <div className="rounded-[30px] border border-slate-300 bg-white p-7 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-100 text-slate-400 ring-1 ring-slate-200">
          <FaUserCircle size={28} />
        </div>

        <h3 className="text-sm font-bold text-slate-800">
          Nenhum contato selecionado
        </h3>

        <p className="mt-2 max-w-[230px] text-xs leading-5 text-slate-500">
          Os detalhes importantes da lead aparecerão aqui.
        </p>
      </div>
    </div>
  );
}

function EmpresaMismatch({ conversa, empresaId, conversaEmpresaId }) {
  return (
    <div className="flex h-full w-full items-center justify-center bg-white px-6">
      <div className="max-w-sm rounded-[32px] border border-red-200 bg-white p-7 text-center shadow-xl">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-3xl bg-red-50 text-red-600 ring-1 ring-red-200">
          <FaLock size={24} />
        </div>

        <h3 className="text-sm font-bold text-slate-900">Conversa bloqueada</h3>

        <p className="mt-2 text-xs leading-5 text-slate-500">
          Esta conversa não pertence à empresa ativa.
        </p>

        <div className="mt-5 rounded-2xl bg-slate-50 px-4 py-3 text-left text-xs text-slate-500 ring-1 ring-slate-200">
          <div>
            <span className="font-bold text-slate-700">Empresa ativa:</span>{" "}
            {empresaId || "não identificada"}
          </div>

          <div className="mt-1">
            <span className="font-bold text-slate-700">
              Empresa da conversa:
            </span>{" "}
            {conversaEmpresaId || "não informada"}
          </div>

          <div className="mt-1">
            <span className="font-bold text-slate-700">Conversa:</span>{" "}
            {conversa?.id || "não identificada"}
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ icon, title }) {
  return (
    <div className="flex items-center gap-2">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-500 ring-1 ring-slate-200">
        {icon}
      </span>

      <h3 className="truncate text-sm font-bold text-slate-900">{title}</h3>
    </div>
  );
}

function MiniMetric({ icon, label, value, className }) {
  return (
    <div className={`rounded-2xl border px-3 py-3 ${className}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wide opacity-65">
        {icon}
        {label}
      </div>

      <div className="mt-1 truncate text-xs font-black">{value || "—"}</div>
    </div>
  );
}

function InfoPill({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl bg-slate-50 px-3 py-3 text-xs ring-1 ring-slate-200">
      <span className="font-black uppercase tracking-wide text-slate-400">
        {label}
      </span>
      <span className="truncate font-bold text-slate-700">
        {value || "Não informado"}
      </span>
    </div>
  );
}

function OperationalCard({
  icon,
  title,
  item,
  fallback,
  dateFields = [],
  textFields = [],
}) {
  const data = getFirstValue(item, dateFields);
  const texto = getFirstValue(item, textFields);
  const style = getOperationalCardStyle(title);

  return (
    <div className={`rounded-2xl border px-3 py-3 shadow-sm ${style.card}`}>
      <div className="flex items-start gap-3">
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-2xl ring-1 ${style.icon}`}
        >
          {icon}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className={`text-xs font-black ${style.title}`}>{title}</div>

            {data && (
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-black ring-1 ${style.date}`}
              >
                {formatDate(data)}
              </span>
            )}
          </div>

          <p
            className={`mt-1 line-clamp-2 text-xs font-medium leading-5 ${style.text}`}
          >
            {texto || fallback}
          </p>
        </div>
      </div>
    </div>
  );
}

function getOperationalCardStyle(title) {
  const normalized = normalizar(title);

  if (normalized.includes("interacao")) {
    return {
      card: "border-blue-200 bg-blue-50/70",
      icon: "bg-white text-blue-700 ring-blue-200",
      title: "text-blue-900",
      date: "bg-white text-blue-700 ring-blue-200",
      text: "text-blue-800/80",
    };
  }

  if (normalized.includes("tarefa")) {
    return {
      card: "border-amber-200 bg-amber-50/80",
      icon: "bg-white text-amber-700 ring-amber-200",
      title: "text-amber-900",
      date: "bg-white text-amber-700 ring-amber-200",
      text: "text-amber-800/85",
    };
  }

  if (normalized.includes("agendamento")) {
    return {
      card: "border-emerald-200 bg-emerald-50/80",
      icon: "bg-white text-emerald-700 ring-emerald-200",
      title: "text-emerald-900",
      date: "bg-white text-emerald-700 ring-emerald-200",
      text: "text-emerald-800/85",
    };
  }

  return {
    card: "border-slate-200 bg-slate-50",
    icon: "bg-white text-slate-500 ring-slate-200",
    title: "text-slate-800",
    date: "bg-white text-slate-400 ring-slate-200",
    text: "text-slate-500",
  };
}

function CanalIcon({ canal, size = 14 }) {
  const normalized = String(canal || "").toLowerCase();

  if (normalized.includes("whatsapp")) {
    return <FaWhatsapp className="text-[#25D366]" size={size} />;
  }

  if (normalized.includes("instagram")) {
    return <FaInstagram className="text-[#C13584]" size={size} />;
  }

  if (normalized.includes("facebook") || normalized.includes("messenger")) {
    return <FaFacebookMessenger className="text-[#1877F2]" size={size} />;
  }

  if (normalized.includes("telegram")) {
    return <FaTelegramPlane className="text-[#229ED9]" size={size} />;
  }

  return <FaInbox className="text-slate-500" size={size} />;
}

function getCanalStyle(canal) {
  const normalized = String(canal || "").toLowerCase();

  if (normalized.includes("whatsapp")) {
    return {
      accent: "bg-[#25D366]",
      avatarRing: "ring-emerald-300",
      avatarBg: "bg-emerald-50 text-[#128C7E] ring-emerald-300",
      iconBg: "bg-emerald-50",
      badge: "border-emerald-200 bg-emerald-50 text-[#128C7E]",
    };
  }

  if (normalized.includes("instagram")) {
    return {
      accent: "bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#FCAF45]",
      avatarRing: "ring-pink-300",
      avatarBg:
        "bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 text-[#C13584] ring-pink-300",
      iconBg: "bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50",
      badge:
        "border-pink-200 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 text-[#C13584]",
    };
  }

  if (normalized.includes("facebook") || normalized.includes("messenger")) {
    return {
      accent: "bg-[#1877F2]",
      avatarRing: "ring-blue-300",
      avatarBg: "bg-blue-50 text-[#1877F2] ring-blue-300",
      iconBg: "bg-blue-50",
      badge: "border-blue-200 bg-blue-50 text-[#1877F2]",
    };
  }

  if (normalized.includes("telegram")) {
    return {
      accent: "bg-[#229ED9]",
      avatarRing: "ring-sky-300",
      avatarBg: "bg-sky-50 text-[#229ED9] ring-sky-300",
      iconBg: "bg-sky-50",
      badge: "border-sky-200 bg-sky-50 text-[#229ED9]",
    };
  }

  return {
    accent: "bg-slate-400",
    avatarRing: "ring-slate-300",
    avatarBg: "bg-slate-100 text-slate-500 ring-slate-300",
    iconBg: "bg-slate-50",
    badge: "border-slate-200 bg-slate-50 text-slate-700",
  };
}

function getStatusStyle(status) {
  const normalized = normalizar(status);

  if (normalized.includes("reativ")) {
    return {
      icon: FaRedoAlt,
      badge: "border-orange-200 bg-orange-50 text-orange-700",
      metric: "border-orange-200 bg-orange-50 text-orange-800",
    };
  }

  if (normalized.includes("prospect")) {
    return {
      icon: FaChartLine,
      badge: "border-blue-200 bg-blue-50 text-blue-700",
      metric: "border-blue-200 bg-blue-50 text-blue-800",
    };
  }

  if (normalized.includes("negoci")) {
    return {
      icon: FaHandshake,
      badge: "border-violet-200 bg-violet-50 text-violet-700",
      metric: "border-violet-200 bg-violet-50 text-violet-800",
    };
  }

  if (normalized.includes("agend")) {
    return {
      icon: FaCalendarAlt,
      badge: "border-emerald-200 bg-emerald-50 text-emerald-700",
      metric: "border-emerald-200 bg-emerald-50 text-emerald-800",
    };
  }

  if (normalized.includes("comparec")) {
    return {
      icon: FaCheckCircle,
      badge: "border-green-200 bg-green-50 text-green-700",
      metric: "border-green-200 bg-green-50 text-green-800",
    };
  }

  if (normalized.includes("falt")) {
    return {
      icon: FaExclamationTriangle,
      badge: "border-rose-200 bg-rose-50 text-rose-700",
      metric: "border-rose-200 bg-rose-50 text-rose-800",
    };
  }

  if (
    normalized.includes("perdid") ||
    normalized.includes("cancel") ||
    normalized.includes("descart") ||
    normalized.includes("inativ") ||
    normalized.includes("desqual")
  ) {
    return {
      icon: FaLock,
      badge: "border-slate-200 bg-slate-100 text-slate-600",
      metric: "border-slate-200 bg-slate-100 text-slate-700",
    };
  }

  return {
    icon: FaTag,
    badge: "border-slate-200 bg-slate-50 text-slate-600",
    metric: "border-slate-200 bg-slate-50 text-slate-700",
  };
}

function formatCanal(canal) {
  const normalized = String(canal || "").toLowerCase();

  if (normalized.includes("whatsapp")) return "WhatsApp";
  if (normalized.includes("instagram")) return "Instagram";
  if (normalized.includes("facebook")) return "Facebook";
  if (normalized.includes("messenger")) return "Messenger";
  if (normalized.includes("telegram")) return "Telegram";

  return canal || "Canal";
}

function limparStatus(status) {
  if (!status) return "Sem status";

  const limpo = String(status)
    .replace(/^negocia[cç][aã]o\s*:\s*/i, "")
    .replace(/^status\s*:\s*/i, "")
    .replace(/^lead\s*:\s*/i, "")
    .replaceAll("_", " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

  if (normalizar(limpo).includes("reativ")) {
    return "Lead antiga";
  }

  return limpo;
}

function moeda(v) {
  const value = toMoneyNum(v);

  if (!value) return "—";

  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });
}

function toMoneyNum(v) {
  if (v == null || v === "") return 0;
  if (typeof v === "number" && Number.isFinite(v)) return v;

  const s = String(v)
    .replace(/\s|R\$\s?/g, "")
    .trim();

  if (/^[\d.]+,\d{2}$/.test(s)) {
    return Number(s.replace(/\./g, "").replace(",", "."));
  }

  const n = Number(s.replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function formatDate(value) {
  if (!value) return "";

  if (typeof value === "string" && /^\d{2}\/\d{2}\/\d{4}/.test(value)) {
    return value;
  }

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [year, month, day] = value.slice(0, 10).split("-");
    return `${day}/${month}/${year}`;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString("pt-BR");
}

function getNomeById(id, lista, fields = ["nome"]) {
  if (!id || !Array.isArray(lista)) return "";

  const item = lista.find((x) => String(x.id) === String(id));

  if (!item) return "";

  for (const field of fields) {
    if (item[field]) return item[field];
  }

  return "";
}

function getNomeUsuario(id, usuarios) {
  if (!id || !Array.isArray(usuarios)) return "";

  const usuario = usuarios.find((u) => String(u.id) === String(id));

  return usuario?.nome || usuario?.name || "";
}

function getUltimoItem(lista, dateFields = []) {
  if (!Array.isArray(lista) || lista.length === 0) return null;

  return [...lista].sort((a, b) => {
    const dateA = new Date(getFirstValue(a, dateFields) || 0).getTime();
    const dateB = new Date(getFirstValue(b, dateFields) || 0).getTime();

    return (
      (Number.isNaN(dateB) ? 0 : dateB) - (Number.isNaN(dateA) ? 0 : dateA)
    );
  })[0];
}

function getProximaTarefa(lista) {
  if (!Array.isArray(lista) || lista.length === 0) return null;

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  const tarefasFuturas = lista
    .filter((tarefa) => {
      const status = String(tarefa?.status || "").toLowerCase();
      const concluida = [
        "concluida",
        "concluido",
        "finalizada",
        "finalizado",
        "feito",
        "feita",
      ].includes(status);

      if (concluida) return false;

      const data = new Date(tarefa?.data_vencimento || tarefa?.data || 0);

      if (Number.isNaN(data.getTime())) return true;

      return data >= hoje;
    })
    .sort((a, b) => {
      const dateA = new Date(a?.data_vencimento || a?.data || 0).getTime();
      const dateB = new Date(b?.data_vencimento || b?.data || 0).getTime();

      return (
        (Number.isNaN(dateA) ? 0 : dateA) - (Number.isNaN(dateB) ? 0 : dateB)
      );
    });

  return tarefasFuturas[0] || null;
}

function getLeadIdLeadDetails(lead = {}) {
  return lead?.id || lead?.lead_id || lead?.leadId || lead?.id_lead || null;
}

function getLeadContextoIdLeadDetails(conversa = {}) {
  return (
    conversa?.lead_contexto_id ||
    conversa?.leadContextoId ||
    conversa?.lead_contexto?.id ||
    conversa?.leadContexto?.id ||
    null
  );
}

function getLeadsVinculadasLeadDetails(conversa = {}) {
  const candidatos = [
    conversa?.leads_vinculadas,
    conversa?.leadsVinculadas,
    conversa?.leads_relacionadas,
    conversa?.leadsRelacionadas,
    conversa?.telefone_leads,
    conversa?.telefoneLeads,
  ];

  for (const lista of candidatos) {
    if (Array.isArray(lista) && lista.length) return lista.filter(Boolean);
  }

  const lead = conversa?.lead || conversa?.lead_dados || conversa?.leadDados;
  return lead ? [lead] : [];
}

function getNomeLeadVinculadaLeadDetails(lead = {}) {
  return (
    lead?.nome_lead ||
    lead?.nomeLead ||
    lead?.nome ||
    lead?.nome_cliente ||
    lead?.paciente ||
    lead?.telefone1 ||
    lead?.telefone ||
    `Lead #${getLeadIdLeadDetails(lead) || ""}`.trim() ||
    "Lead vinculada"
  );
}

function normalizarTelefoneLeadDetails(value = "") {
  return String(value || "").replace(/\D/g, "");
}

function ultimosDigitosTelefoneLeadDetails(value = "", tamanho = 8) {
  return normalizarTelefoneLeadDetails(value).slice(-tamanho);
}

function leadPertenceAoTelefoneLeadDetails(lead = {}, telefone = "") {
  const telefoneConversa11 = ultimosDigitosTelefoneLeadDetails(telefone, 11);
  const telefoneConversa8 = ultimosDigitosTelefoneLeadDetails(telefone, 8);

  if (!telefoneConversa11 && !telefoneConversa8) return true;

  const telefonesLead = [
    lead?.telefone1,
    lead?.telefone2,
    lead?.telefone,
    lead?.celular,
    lead?.whatsapp,
    lead?.numero,
  ]
    .map((item) => normalizarTelefoneLeadDetails(item))
    .filter(Boolean);

  if (!telefonesLead.length) return false;

  return telefonesLead.some((tel) => {
    const tel11 = tel.slice(-11);
    const tel8 = tel.slice(-8);
    return (
      (telefoneConversa11 && tel11 === telefoneConversa11) ||
      (telefoneConversa8 && tel8 === telefoneConversa8)
    );
  });
}

function getFirstValue(obj, fields = []) {
  if (!obj) return "";

  for (const field of fields) {
    if (obj[field]) return obj[field];
  }

  return "";
}

function getTemConversaAtiva(conversa) {
  if (!conversa) return false;

  if (conversa.sem_conversa || conversa.semConversa) return false;
  if (conversa.conversa_ativa === false || conversa.conversaAtiva === false) {
    return false;
  }

  if (conversa.conversa_ativa === true || conversa.conversaAtiva === true) {
    return true;
  }

  const id = String(conversa.id || "");
  const idVirtual =
    id.startsWith("lead-") ||
    id.startsWith("contato-") ||
    id.startsWith("nova-");

  if (idVirtual) return false;

  if (conversa.conversa_id || conversa.conversaId) return true;
  if (conversa.ticket_id || conversa.ticketId) return true;

  const mensagens = Array.isArray(conversa.mensagens) ? conversa.mensagens : [];

  if (mensagens.length > 0) return true;

  const status = String(conversa.status || "").toLowerCase();

  if (
    status.includes("atendimento") ||
    status.includes("aguardando") ||
    status.includes("fila") ||
    status.includes("aberta") ||
    status.includes("ativa")
  ) {
    return true;
  }

  return false;
}

function montarPayloadAtualizacaoLead({
  lead,
  conversa,
  empresaId,
  usuarioLogado,
  formEdicaoLead,
}) {
  const status = String(formEdicaoLead.status_negociacao || "").trim();

  return limparObjetoVazio({
    empresa_id:
      Number(lead?.empresa_id || conversa?.empresa_id || empresaId) || null,
    id_usuario:
      Number(
        lead?.id_usuario ||
          lead?.captador_id ||
          lead?.usuario_criador_id ||
          conversa?.lead_captador_id ||
          conversa?.dono_lead_id ||
          usuarioLogado?.id ||
          conversa?.usuario_id,
      ) || null,
    usuario_criador_id:
      Number(
        lead?.usuario_criador_id ||
          lead?.id_usuario ||
          lead?.captador_id ||
          conversa?.lead_captador_id ||
          conversa?.dono_lead_id ||
          usuarioLogado?.id,
      ) || null,
    nome_lead: lead?.nome_lead || lead?.nome || conversa?.nome || "",
    data_lead: normalizarDataLead(
      lead?.data_lead || lead?.criado_em || conversa?.created_at || new Date(),
    ),
    id_procedimento:
      Number(formEdicaoLead.id_procedimento || lead?.id_procedimento) || null,
    telefone1:
      lead?.telefone1 ||
      lead?.telefone ||
      lead?.celular ||
      lead?.whatsapp ||
      conversa?.numero ||
      conversa?.telefone ||
      "",
    total_orcamento: toMoneyNum(formEdicaoLead.total_orcamento),
    total_contratado: toMoneyNum(lead?.total_contratado),
    data_agendamento: normalizarDataLead(lead?.data_agendamento),
    id_regiao: Number(formEdicaoLead.id_regiao || lead?.id_regiao) || null,
    id_dentista:
      Number(formEdicaoLead.id_dentista || lead?.id_dentista) || null,
    id_fonte_lead:
      Number(formEdicaoLead.id_fonte_lead || lead?.id_fonte_lead) || null,
    id_campanha:
      Number(formEdicaoLead.id_campanha || lead?.id_campanha) || null,
    status_agendamento: lead?.status_agendamento || "",
    potencial: formEdicaoLead.potencial || lead?.potencial || "",
    status_negociacao: status,
  });
}

function limparObjetoVazio(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => {
      if (value === undefined || value === null) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      return true;
    }),
  );
}

function normalizarDataLead(value) {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(
      2,
      "0",
    )}-${String(value.getDate()).padStart(2, "0")} 00:00:00`;
  }

  const raw = String(value).trim();

  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(raw)) return raw;
  if (/^\d{4}-\d{2}-\d{2}T/.test(raw))
    return raw.replace("T", " ").slice(0, 19);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return `${raw} 00:00:00`;

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(date.getDate()).padStart(2, "0")} 00:00:00`;
}

function formatMoneyInput(value) {
  if (value == null || value === "") return "";

  const digits = String(value).replace(/\D/g, "");

  if (!digits) return "";

  const number = Number(digits) / 100;

  return number.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function getLeadUrl(leadId, lead = {}, conversa = {}) {
  if (!leadId) return "";

  const base = window.location.origin;
  const params = new URLSearchParams();

  params.set("lead_id", String(leadId));

  const telefone =
    lead?.telefone1 ||
    lead?.telefone ||
    lead?.celular ||
    lead?.whatsapp ||
    conversa?.numero ||
    conversa?.telefone ||
    "";

  const nome = lead?.nome_lead || lead?.nome || conversa?.nome || "";

  if (telefone) params.set("telefone", String(telefone).replace(/\D/g, ""));
  if (nome) params.set("nome", nome);

  return `${base}/leadsspeed?${params.toString()}`;
}

function getIniciaisNome(nome = "") {
  const partes = String(nome || "")
    .trim()
    .replace(/\s+/g, " ")
    .split(" ")
    .filter(Boolean);

  if (partes.length === 0) return "?";
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();

  return `${partes[0][0] || ""}${partes[partes.length - 1][0] || ""}`.toUpperCase();
}

function formatTelefoneBRLeadDetails(value = "") {
  const digits = String(value || "").replace(/\D/g, "");
  const semPais =
    digits.startsWith("55") && digits.length > 11 ? digits.slice(2) : digits;

  if (semPais.length === 11) {
    return `(${semPais.slice(0, 2)}) ${semPais.slice(2, 7)}-${semPais.slice(7)}`;
  }

  if (semPais.length === 10) {
    return `(${semPais.slice(0, 2)}) ${semPais.slice(2, 6)}-${semPais.slice(6)}`;
  }

  return value || "Não informado";
}

function getDataCriacaoLead(conversa = {}, lead = {}) {
  const raw =
    lead?.data_lead ||
    lead?.criado_em ||
    lead?.created_at ||
    lead?.data_cadastro ||
    conversa?.data_lead ||
    conversa?.criado_em ||
    conversa?.created_at ||
    conversa?.aberto_em ||
    "";

  if (!raw) return "";

  return formatDate(raw);
}

function getCriadorLeadNome({
  conversa = {},
  lead = {},
  usuarios = [],
  fallback = "",
} = {}) {
  const criadorId =
    lead?.id_usuario ||
    lead?.captador_id ||
    lead?.usuario_criador_id ||
    conversa?.lead_captador_id ||
    conversa?.dono_lead_id ||
    null;

  return (
    lead?.captador_nome ||
    lead?.usuario_nome ||
    lead?.usuario_criador_nome ||
    conversa?.lead_captador_nome ||
    conversa?.dono_lead_nome ||
    getNomeUsuario(criadorId, usuarios) ||
    fallback ||
    ""
  );
}

function getEmpresaNomeVisivel(empresaNome = "", empresaId = null) {
  const nome = String(empresaNome || "").trim();

  if (!nome) return "";
  if (empresaId && nome.toLowerCase() === `empresa ${empresaId}`.toLowerCase())
    return "";
  if (/^empresa\s+\d+$/i.test(nome)) return "";

  return nome;
}

function normalizar(valor) {
  return String(valor || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}
